import { useEffect, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../../api";
import { Card } from "../../../components/ui/CCard";
import ManualReviewBadge from "../../../components/ui/ManualReviewBadge";
import { Eye, SlidersHorizontal } from "lucide-react";

export default function ConsultationHistory() {
  const [searchParams] = useSearchParams();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Review Edit Modal State
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);

  // Pagination & Filter State
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    patient_name: "",
    status: searchParams.get("status") || "",
    doctor_id: searchParams.get("doctor_id") || "",
    review_required: ""
  });
  const [doctors, setDoctors] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  let isSiPresent = false;
  let isSiAssessed = false;

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch doctors for filter if admin
        const auth = localStorage.getItem('auth');
        let userRole = '';
        if (auth) {
          const { role } = JSON.parse(auth);
          userRole = role.toLowerCase();
          if (userRole === 'admin' || userRole === 'ops') {
            setIsAdmin(true);
            const drRes = await api.getDoctorsList();
            setDoctors(drRes.data.doctors || []);
          } else if (userRole === 'supervisor') {
            setIsSupervisor(true);
            const repRes = await api.getSupervisorReportees();
            // Shape to match the doctor filter dropdown structure
            setDoctors(repRes.data.map(r => ({ doctor_id: r.reportee_id, name: r.name })));
          }
        }
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };

    fetchInitialData();
  }, []);

  const fetchConsultations = async () => {
    setLoading(true);
    try {
      const res = await api.getAllConsultations({
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      });
      // The new backend response format is { data: [...], total, ... }
      setConsultations(res.data.data || []);
      setPagination(prev => ({
        ...prev,
        total: res.data.total,
        pages: res.data.pages
      }));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsultations();
  }, [pagination.page, pagination.limit, filters]);

  const openReviewModal = (session) => {
    setActiveSession(session);
    setReviewComment(session.review_comment || "");
    setShowReviewModal(true);
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) setShowReviewModal(false);
    };
    if (showReviewModal) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [showReviewModal]);

  const submitReview = async () => {
    if (!activeSession) return;

    try {
      await api.markSessionAsReviewed(activeSession.id, undefined, reviewComment);

      // Update local state
      setConsultations(consultations.map(c =>
        c.id === activeSession.id
          ? { 
              ...c, 
              review_comment: reviewComment, 
              review_status: c.review_required ? 'reviewed' : c.review_status,
              reviewed_on: new Date().toISOString()
            }
          : c
      ));

      setShowReviewModal(false);
      setActiveSession(null);
      setReviewComment("");
      alert("Review updated successfully");
    } catch (err) {
      alert(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-secondary-900 dark:text-secondary-100">Consultation History</h1>
          <p className="text-secondary-500 dark:text-secondary-400 mt-1">Manage and review your patient sessions</p>
        </div>
      </div>

      {/* Filters Bar */}
      <Card className="mb-6 p-4">
        {/* Patient Name + mobile filter toggle */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs font-bold text-secondary-500 uppercase mb-1">Patient Name</label>
            <input
              type="text"
              placeholder="Search patient..."
              className="w-full px-3 py-2 rounded-lg border border-secondary-200 text-sm focus:ring-2 focus:ring-primary-100 outline-none placeholder:text-secondary-400"
              value={filters.patient_name}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, patient_name: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            />
          </div>
          <button
            onClick={() => setShowMobileFilters(p => !p)}
            className={`sm:hidden flex-shrink-0 p-2.5 rounded-lg border text-sm transition-colors ${showMobileFilters ? 'bg-primary-50 border-primary-300 text-primary-600' : 'border-secondary-200 text-secondary-500 hover:bg-secondary-50'}`}
            title="More filters"
          >
            <SlidersHorizontal size={16} />
          </button>
        </div>

        {/* Extra filters: always visible on sm+, collapsible on mobile */}
        <div className={`${showMobileFilters ? 'flex' : 'hidden'} sm:flex flex-col sm:flex-row sm:flex-wrap lg:flex-nowrap gap-4 mt-4`}>
          <div className="sm:flex-1 lg:flex-[2]">
            <label className="block text-xs font-bold text-secondary-500 uppercase mb-1">Date Range</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-secondary-200 text-sm focus:ring-2 focus:ring-primary-100 outline-none"
                value={filters.from_date}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, from_date: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
              <span className="text-secondary-400 flex-shrink-0 text-xs font-bold">to</span>
              <input
                type="date"
                className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-secondary-200 text-sm focus:ring-2 focus:ring-primary-100 outline-none"
                value={filters.to_date}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, to_date: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
            </div>
          </div>

          {(isAdmin || isSupervisor) && (
            <div className="sm:flex-1">
              <label className="block text-xs font-bold text-secondary-500 uppercase mb-1">
                {isSupervisor ? 'Clinician' : 'Doctor'}
              </label>
              <select
                className="w-full px-3 py-2 rounded-lg border border-secondary-200 text-sm focus:ring-2 focus:ring-primary-100 outline-none bg-white"
                value={filters.doctor_id}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, doctor_id: e.target.value }));
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              >
                <option value="">{isSupervisor ? 'All My Clinicians' : 'All Doctors'}</option>
                {doctors.map(dr => (
                  <option key={dr.doctor_id || dr.id} value={dr.doctor_id || dr.id}>{dr.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="sm:flex-1">
            <label className="block text-xs font-bold text-secondary-500 uppercase mb-1">Review Status</label>
            <select
              className="w-full px-3 py-2 rounded-lg border border-secondary-200 text-sm focus:ring-2 focus:ring-primary-100 outline-none bg-white"
              value={filters.status}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, status: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
            </select>
          </div>

          <div className="sm:flex-1">
            <label className="block text-xs font-bold text-secondary-500 uppercase mb-1">Review Required</label>
            <select
              className="w-full px-3 py-2 rounded-lg border border-secondary-200 text-sm focus:ring-2 focus:ring-primary-100 outline-none bg-white"
              value={filters.review_required}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, review_required: e.target.value }));
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        </div>

        <div className={`mt-4 flex justify-end ${!showMobileFilters ? 'hidden sm:flex' : ''}`}>
          <button
            onClick={() => {
              setFilters({ from_date: "", to_date: "", patient_name: "", status: "", doctor_id: "", review_required: "" });
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </Card>

      {loading ? (
        <Card className="p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-secondary-500 font-medium">Loading consultations...</p>
        </Card>
      ) : error ? (
        <Card className="p-8 border-red-200 bg-red-50/50">
          <p className="text-red-500 font-medium text-center">Error: {error}</p>
        </Card>
      ) : consultations.length === 0 ? (
        <Card className="p-12 text-center text-secondary-500">
          <div className="text-6xl mb-4">📭</div>
          <p className="text-lg">No consultations found.</p>
          <p className="text-sm mt-2">Try adjusting your filters or start a new session.</p>
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white shadow-soft rounded-xl overflow-x-auto border border-secondary-100">
            <div className="p-4">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-secondary-200">
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">ID</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Date</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider">Patient</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider min-w-48">Doctor</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider w-28">Review</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider text-center w-20">SI</th>
                    <th className="py-3 px-4 text-xs font-semibold text-secondary-500 uppercase tracking-wider text-right pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {consultations.map((c, index) => {
                    const displayId = pagination.total - ((pagination.page - 1) * pagination.limit) - index;
                    let meta = null;
                    try {
                      if (c.supervisor_evaluation) {
                        const evalData = typeof c.supervisor_evaluation === 'string'
                          ? JSON.parse(c.supervisor_evaluation)
                          : c.supervisor_evaluation;
                        meta = evalData?.review_meta;
                      }
                    } catch (e) {
                      console.error('Error parsing evaluation data:', e);
                    }

                    return (
                      <tr key={c.id} className="border-b border-secondary-100 hover:bg-secondary-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium font-mono text-xs">#{displayId}</td>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{c.sessiondate || "-"}</td>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium">{c.patients?.name || "Unknown"}</td>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium min-w-48">{c.doctor_name || "Unknown"}</td>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium w-28">
                          {meta ? (
                            <ManualReviewBadge meta={meta} size="sm" showReasons={false} />
                          ) : (
                            <span className="text-xs text-secondary-400">N/A</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-secondary-900 font-medium text-center w-20">
                          {c.is_si_present ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">SI</span>
                          ) : (
                            <span className="text-secondary-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-right pr-4">
                          <div className="flex justify-end gap-2">
                            <Link
                              to={`/consultation/${c.id}`}
                              className="p-1.5 rounded-lg bg-white border border-secondary-200 text-secondary-600 hover:bg-primary-50 hover:text-primary-600 hover:border-primary-200 transition-all shadow-sm"
                              title="View Report"
                            >
                              <Eye size={16} />
                            </Link>
                            {(isAdmin || isSupervisor) && (
                              <button
                                onClick={() => openReviewModal(c)}
                                className={`p-1.5 rounded-lg transition-colors text-xs border relative ${
                                  c.review_required && c.review_status === 'pending' 
                                    ? 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100' 
                                    : c.review_comment 
                                      ? 'bg-primary-50 text-primary-600 border-primary-200 hover:bg-primary-100'
                                      : 'bg-secondary-100 text-secondary-600 border-secondary-200 hover:bg-secondary-200'
                                }`}
                                title={
                                  c.review_required && c.review_status === 'pending' 
                                    ? 'Mark as Reviewed' 
                                    : c.review_comment 
                                      ? 'Edit Comments' 
                                      : 'Add Comments'
                                }
                              >
                                ✏️
                                {c.review_comment && !c.review_required && (
                                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-primary-500 rounded-full border border-white"></span>
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {consultations.map((c, index) => {
              const displayId = pagination.total - ((pagination.page - 1) * pagination.limit) - index;
              return (
              <Card key={c.id} className="p-5">
                <div className="space-y-4">
                  <div className="flex items-center justify-between border-b border-secondary-100 pb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-secondary-400 font-bold uppercase tracking-wider">ID</span>
                      <span className="text-sm font-bold text-secondary-900">#{displayId}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="block text-xs text-secondary-400 mb-1">Date</span>
                      <span className="block text-sm text-secondary-700 font-medium">{c.sessiondate || "-"}</span>
                    </div>
                    <div>
                      <span className="block text-xs text-secondary-400 mb-1">Patient</span>
                      <span className="block text-sm text-secondary-700 font-medium">{c.patients?.name || "Unknown"}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="block text-xs text-secondary-400 mb-1">Doctor</span>
                      <span className="block text-sm text-secondary-700 font-medium">{c.doctor_name || "Unknown"}</span>
                    </div>
                  </div>
                  {c.is_si_present && (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">SI Detected</span>
                    </div>
                  )}

                  {/* Mobile Actions for Supervisors/Admins */}
                  {(isAdmin || isSupervisor) && (
                    <div className="pt-2">
                        <button
                           onClick={() => openReviewModal(c)}
                           className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm flex items-center justify-center gap-2 ${
                             c.review_required && c.review_status === 'pending' 
                               ? 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200' 
                               : c.review_comment 
                                 ? 'bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-200'
                                 : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                           }`}
                         >
                           ✏️ {
                             c.review_required && c.review_status === 'pending' 
                               ? 'Mark as Reviewed' 
                               : c.review_comment 
                                 ? 'Edit Comments' 
                                 : 'Add Comments'
                           }
                        </button>
                    </div>
                  )}

                  <div className="pt-3">
                    <Link
                      to={`/consultation/${c.id}`}
                      className="flex items-center justify-center gap-2 w-full bg-primary-100 text-primary-700 px-4 py-3 rounded-xl hover:bg-primary-200 transition-colors font-semibold shadow-sm"
                    >
                      <Eye size={15} /> View Report
                    </Link>
                  </div>
                </div>
              </Card>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {pagination.pages > 1 && (
            <div className="mt-8 flex items-center justify-between bg-white px-4 py-3 sm:px-6 rounded-xl border border-secondary-200 shadow-sm">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                  disabled={pagination.page === pagination.pages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-secondary-700 hover:bg-secondary-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-secondary-700">
                    Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of{' '}
                    <span className="font-medium">{pagination.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-secondary-400 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    >
                      <span className="sr-only">Previous</span>
                      &larr;
                    </button>
                    {[...Array(pagination.pages)].map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${pagination.page === i + 1 ? 'z-10 bg-primary-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600' : 'text-secondary-900 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0'}`}
                      >
                        {i + 1}
                      </button>
                    )).slice(Math.max(0, pagination.page - 3), Math.min(pagination.pages, pagination.page + 2))}
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.pages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-secondary-400 ring-1 ring-inset ring-secondary-300 hover:bg-secondary-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                    >
                      <span className="sr-only">Next</span>
                      &rarr;
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Review Edit Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-secondary-100">
              <h3 className="text-lg font-bold text-secondary-900">
                {activeSession?.review_status === 'reviewed' ? 'Edit Session Review' : 'Mark Session as Reviewed'}
              </h3>
              <p className="text-sm text-secondary-500 mt-1">
                {activeSession?.review_status === 'reviewed' 
                  ? `Update the review comment for Session #${activeSession?.id}`
                  : `Add a comment to complete the review for Session #${activeSession?.id}`
                }
              </p>
            </div>

            <div className="p-6 pb-2">
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Review Comment
              </label>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Enter review comments..."
                className="w-full h-32 px-4 py-3 rounded-lg border border-secondary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 outline-none transition-all resize-none text-sm"
                autoFocus
              />
            </div>

            {/* Last Reviewer Info */}
            <div className="px-6 pb-6 pt-2">
              <div className="bg-secondary-50 rounded-lg p-3 text-xs text-secondary-600 border border-secondary-100 flex justify-between items-center">
                <div>
                  <span className="font-semibold block text-secondary-700">Last Reviewed By:</span>
                  <span>
                    {(() => {
                      try {
                        if (!activeSession) return "Unknown";
                        const evalData = typeof activeSession.supervisor_evaluation === 'string'
                          ? JSON.parse(activeSession.supervisor_evaluation)
                          : activeSession.supervisor_evaluation;
                        return evalData?.review_meta?.reviewed_by || evalData?.meta?.reviewed_by || "—";
                      } catch { return "Admin"; }
                    })()}
                  </span>
                </div>
                <div className="text-right">
                  <span className="font-semibold block text-secondary-700">Date:</span>
                  <span>{activeSession?.reviewed_on ? new Date(activeSession.reviewed_on).toLocaleDateString() : "N/A"}</span>
                </div>
              </div>
            </div>

            <div className="p-6 bg-secondary-50 border-t border-secondary-100 flex justify-end gap-3">
              <button
                onClick={() => setShowReviewModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-secondary-600 hover:text-secondary-900 hover:bg-secondary-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitReview}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 shadow-sm transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
